// src/pages/gallery/Gallery.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import MantisDataTable from "../../components/common/MantisDataTable";
import DocumentFormModal from "./DocumentFormModal";
import { fetchDocuments } from "../../api/documentApi";
import { toast } from "react-toastify";
import { getProxyImageUrl } from "../../utils/common";

const BASE_IMAGE_URL = import.meta.env.VITE_BASE_IMAGE_URL || "";

// simple date formatter (YYYY-MM-DD)
const formatDate = (dateString) => {
  if (!dateString) return "—";
  return String(dateString).split("T")[0];
};

export default function Gallery() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // modal state
  const [openForm, setOpenForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);

  // preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewType, setPreviewType] = useState(""); // "image" | "pdf" | "other"

  const normalizeUrl = (url) => {
    if (!url) return "";
    return String(url).startsWith("http") ? url : `${BASE_IMAGE_URL}${url}`;
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await fetchDocuments();
      const normalized = (res || []).map((doc) => ({
        ...doc,
        document_url: normalizeUrl(doc.document_url),
        added_date: doc.added_date ? String(doc.added_date).split("T")[0] : "—",
        updated_date: doc.updated_date ? String(doc.updated_date).split("T")[0] : "—",
      }));
      setDocuments(normalized);
    } catch (err) {
      console.error("Error fetching documents:", err);
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleOpenNew = () => {
    setEditDoc(null);
    setOpenForm(true);
  };

  const handleEdit = (row) => {
    setEditDoc({
      trId: row.tr_id ?? 0,
      documentName: row.document_name,
      documentUrl: row.document_url,
      status: row.status ?? "Active",
      addedBy: row.added_by,
      updatedBy: row.updated_by,
    });
    setOpenForm(true);
  };

  const handleView = (row) => {
    const url = row.document_url;
    if (!url) {
      toast.info("No file URL available for preview");
      return;
    }

    const ext = url.split(".").pop()?.toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
      setPreviewType("image");
    } else if (ext === "pdf") {
      setPreviewType("pdf");
    } else {
      setPreviewType("other");
    }

    setPreviewSrc(url);
    setPreviewOpen(true);
  };

  const handleDownload = (row) => {
    const url = getProxyImageUrl(row.document_url);
    if (!url) {
      toast.info("No file URL to download");
      return;
    }
    window.open(url, "_blank");
  };

  const columns = [
    {
      label: "Action",
      key: "action",
      type: "actions",
      actions: [
        {
          type: "custom",
          label: "View",
          action: handleView,
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          type: "custom",
          label: "Edit",
          action: handleEdit,
          icon: <EditIcon fontSize="small" />,
        },
        {
          type: "custom",
          label: "Download",
          action: handleDownload,
          icon: <DownloadIcon fontSize="small" />,
        },
      ],
    },
    { label: "Name", key: "document_name", type: "text" },
    { label: "Status", key: "status", type: "badge" },
    { label: "Added By", key: "added_by", type: "text" },
    { label: "Added Date", key: "added_date", type: "text" },
    { label: "Updated By", key: "updated_by", type: "text" },
    { label: "Updated Date", key: "updated_date", type: "text" }
  ];

  const handleSaved = () => {
    setOpenForm(false);
    setEditDoc(null);
    loadDocuments();
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "1400px", minHeight: 500 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Document Handbook</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew}>
          Add Document
        </Button>
      </Stack>

      <MantisDataTable
        title="Document List"
        data={documents}
        columns={columns}
        searchable
        searchPlaceholder="Search documents..."
        loading={loading}
      />

      <DocumentFormModal
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSaved={handleSaved}
        initialData={editDoc}
      />

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview</DialogTitle>
        <DialogContent>
          {previewType === "image" && (
            <img
              src={previewSrc}
              alt="preview"
              style={{ width: "100%", height: "auto" }}
            />
          )}
          {previewType === "pdf" && (
            <iframe
              src={previewSrc}
              title="pdf-preview"
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          )}
          {previewType === "other" && (
            <Box>
              <Typography>
                Cannot preview this file type inline. Use download button to open.
              </Typography>
              <Button
                sx={{ mt: 2 }}
                variant="contained"
                onClick={() => window.open(previewSrc, "_blank")}
              >
                Open / Download
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
